import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import * as Sentry from '@sentry/angular';

import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

import { User } from 'src/app/model/settings/user.model';
import { Partner } from 'src/app/model/settings/partner.model';
import { AuthService } from './auth.service';
import { UserService } from 'src/app/service/user.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { PartnerService } from 'src/app/service/partner.service';
import { LoginTimeoutComponent } from 'src/app/route/authentication/login-timeout/login-timeout.component';
import { SettingService } from '../settings/setting.service';
import { clearPendoSession } from 'src/app/util/pendo.util';

import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  public timeout: number = 600000; //default
  warningTimeout = 60000;
  warningInterval: any;
  listenEventsAdded: boolean = false;

  public user = new User();
  public token!: string | undefined;
  public partnerId!: number | undefined;
  private currentVersion: string | undefined;
  private partner = new Partner();
  private partners = new Array<Partner>();
  private currentPartnerObservable: BehaviorSubject<Partner | undefined>;
  private partnerListObservable!: BehaviorSubject<Array<Partner>>;
  private userAction: boolean = false;

  private active: () => void;

  constructor(private partnerService: PartnerService,
              private authService: AuthService,
              private userService: UserService,
              private analytics: AnalyticsService,
              private router: Router,
              private settingService: SettingService,
              private dialogService: MatDialog,
              public snackBar: MatSnackBar) {

    this.currentPartnerObservable = new BehaviorSubject<Partner | undefined>(undefined);
    this.partnerListObservable = new BehaviorSubject<Array<Partner>>(this.partners);

    //setup active function to be used with event listeners
    this.active = (): void => {
      //if logout snackbar is open, do not reset timers, user must click continue
      if (this.snackBar._openedSnackBarRef != null) {
        return;
      }
      this.clearIntervals();
      this.startTimeout();
    }
  }

  public monitorCurrentPartner(): Observable<Partner | undefined> {
    return this.currentPartnerObservable
               .asObservable();
  }

  public monitorPartners(): Observable<Array<Partner>> {
    return this.partnerListObservable
               .asObservable();
  }

  public start(user: User,
               partner: Partner | undefined,
               token: string): void {

    this.partner = partner ? partner : new Partner();
    this.user = user;
    this.token = token;

    // Sentry: attach account metadata (ids only, no PII per NEWTON-470).
    Sentry.setUser({ id: user.userId != null ? String(user.userId) : undefined });
    Sentry.setTag('partner_id', this.partner?.id != null ? String(this.partner.id) : null);

    // Mixpanel: identify + register partner/role super properties (NEWTON-483).
    this.analytics.identify(user, this.partner);

    if(user.partner?.id){
      this.partnerId = user.partner?.id;
    }

    if (this.userService
            .isPortalStaff(this.user)){
      this.loadPartners();
    } else {
      this.currentPartnerObservable
          .next(this.partner);
    }

    this.updateStorage();
  }

  public loadSession(): void {

    const user = sessionStorage.getItem("portal-user");
    const token = sessionStorage.getItem("portal-token");
    const partner = sessionStorage.getItem("portal-partner");
    const partnerId = sessionStorage.getItem("portal-partnerId");

    if (token != null){
      this.token = token;
    }

    if (partnerId != null){
      this.partnerId = Number.parseInt(partnerId);
    }

    if (partner != null){
      this.partner = new Partner().load(JSON.parse(partner));
    }

    if (user != null){
      this.user = new User().load(JSON.parse(user));

      // Sentry: re-attach account metadata on session restore (refresh).
      Sentry.setUser({ id: this.user.userId != null ? String(this.user.userId) : undefined });
      Sentry.setTag('partner_id', this.partner?.id != null ? String(this.partner.id) : null);

      // Mixpanel: re-identify on session restore (refresh).
      this.analytics.identify(this.user, this.partner);

      if (this.userService
              .isPortalStaff(this.user)){
        this.loadPartners();
      } else {
        this.currentPartnerObservable
            .next(this.partner);
      }
    }
  }

  public loadPartners(): void {
      this.partnerService
          .list()
          .subscribe(data => {
            this.partners = new Array<Partner>();

            data.forEach(p => {
              this.partners
                  .push(new Partner().load(p));
            });

            this.currentPartnerObservable
                .next(this.partner);
            this.partnerListObservable
                .next(this.partners);
          });
  }

  public destroySession(): void {
    // Sentry: clear account metadata on logout/session end.
    Sentry.setUser(null);

    // Mixpanel: clear identity + super properties on logout/session end.
    this.analytics.reset();
    this.user = new User();
    this.partner = new Partner();
    this.token = undefined;
    this.partners = new Array();
    this.partnerListObservable.next(this.partners);

    sessionStorage.removeItem("portal-user");
    sessionStorage.removeItem("portal-token");
    sessionStorage.removeItem("portal-partner");
    sessionStorage.removeItem("portal-partnerId");
  }

  public getPartners(enabled?: boolean): Array<Partner> {
    let list: Array<Partner>;

    if (enabled){
      list = this.partners.filter(p => !p.disabled);
    } else {
      list = Array.from(this.partners);
    }

    return list;
  }

  public setPartner(partner: Partner): void {
    this.partner = partner;
    this.partners
        .splice(this.partners
                    .findIndex(p => p.id === partner.id),
                1,
                partner);
    this.updateStorage();
    this.currentPartnerObservable
        .next(partner);

    // Mixpanel: keep partner super properties in sync with the active partner.
    // (Called on both an actual switch and a post-save refresh; the 'Partner
    // Switched' event itself is tracked at the switch UI in home.component.)
    this.analytics.updatePartnerContext(partner);
  }

  public updatePartner(partner: Partner): void {
    const index = this.partners
                      .findIndex((p) => p.id === partner.id)

    // Update our partners collection
    if (index >= 0){
      this.partners
          .splice(index,
                  1,
                  partner);
    } else {
      this.partners
          .push(partner);
    }

    this.partnerListObservable.next(this.partners);

    if(this.partner.id === partner.id){
      this.partner.load(partner);
      this.currentPartnerObservable
          .next(this.partner);
    }

    // Switch to the updated partner
    /*
    this.partner = partner;
    this.updateStorage();
    this.partnerObservable
        .next(partner);
    */
  }

  public assignPartner(partnerId: number): void {
    this.partnerId = partnerId;
    this.updateStorage();
  }

  private updateStorage(): void {
    if (this.user != null){
      sessionStorage.setItem("portal-user", JSON.stringify(this.user))
    }

    if (this.token != null){
      sessionStorage.setItem("portal-token", this.token);
    }

    if (this != null){
      sessionStorage.setItem("portal-partner", JSON.stringify(this.partner));
    }

    if (this.partnerId != null){
      sessionStorage.setItem("portal-partnerId", this.partnerId.toString());
    }
  }

  public isValid(): boolean {
    return this.token !== undefined;
  }

  public hasToken(): boolean {
    return this.isValid();
  }

  public getToken(): string | undefined{
    return this.token;
  }

  public getUser(): User{
    return this.user;
  }

  public hasPartner(): boolean {
    return this.partner?.id !== undefined;
  }

  public getPartner(): Partner {
    return this.partner;
  }

  public isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private clearIntervals(): void {
    clearTimeout(this.warningInterval);
    this.warningInterval = undefined;
  }

  private startTimeout(): void {

    if (this.timeout){

      // This will be trigger when there's one minute left before session expires
      this.warningInterval = setTimeout(() => {

                  const ref = this.snackBar
                                  .openFromComponent(LoginTimeoutComponent,
                                                    { data: 'timeout',
                                                      panelClass: 'customsnackbar'});

                  ref.onAction()
                    .subscribe(() => {
                      //This will only be called if the user clicks the continue button
                      this.userAction = true;
                      this.active();
                    });


                  ref.afterDismissed()
                      .subscribe(() => {
                        //If the user clicks the continue button then we don't want to logout
                        // but have to reset the userAction to false
                        if (!this.userAction) {
                          console.log('Timeout snackbar dismissed with no action');
                          this.logout('Session expired, please login again');
                        } else {
                          this.userAction = false;
                          console.log('Timeout snackbar dismissed with action');
                        }
                      });
                },
                this.timeout - this.warningTimeout);
      }
  }
  public logout(message?: string): void {

    try {

      // Close anything that may still be open
      this.clearIntervals();
      this.removeActiveListeners();
      this.dialogService.closeAll();
      Swal.close();

      if (this.isValid()){

        this.authService
            .logout()
            .subscribe({
                  next: () => {
                    console.log('User logout');
                  },
                  error: (error) => {
                    console.error('Error on logout: ', error);
                  },
              });
        }
    } catch (error) {
      console.error('Logout: ' + error);
    } finally {
      // Only a genuine logout (session existed) — the 401 interceptor also
      // calls logout() on a failed login, which must not emit Logout.
      if (this.isValid()) {
        this.analytics.track('Logout');
      }
      clearPendoSession();
      // Skip the redirect when already on /login (failed-login case) — it fired
      // a spurious Page Viewed; the `message` param isn't read anywhere anyway.
      if (!this.router.url.startsWith('/login')) {
        this.router
            .navigate(['login'],
                      {queryParams: {message},
                       replaceUrl: true});
      }
      this.destroySession();
    }
  }

  public get version(): string | undefined {
    return this.currentVersion;
  }

  public set version(version: string | undefined) {
    this.currentVersion = version;
  }

  private initActiveListeners(): void {
    if (this.listenEventsAdded) {
      return;
    }
    window.addEventListener('pointermove', this.active);
    window.addEventListener('pointerdown', this.active);
    window.addEventListener('keypress', this.active);
    window.addEventListener('wheel', this.active);
    this.listenEventsAdded = true;
  }
s
  private removeActiveListeners() {
    if (!this.listenEventsAdded) {
      return;
    }
    window.removeEventListener('pointermove', this.active);
    window.removeEventListener('pointerdown', this.active);
    window.removeEventListener('keypress', this.active);
    window.removeEventListener('wheel', this.active);
    this.listenEventsAdded = false;
  }

  public initAutoLogout(timeout?: number): void {
    //set timeout here in case of refresh page, start function only runs when logging in
    this.timeout = (timeout ? timeout : 600) * (this.user.email === 'cesarmejia-aquino@ordyx.com' ? 100 : 1000); // Saved in seconds but need it in milliseconds
    this.active();
    this.initActiveListeners();
  }

}
